/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import axios from 'axios';
import { AbstractService } from '../shared/abstract-service';
import type * as Dto from './responses.dto';

export class IdentityProviderService extends AbstractService {
    private readonly authPath: string;
    private readonly mePath: string;
    private readonly invPath: string;
    private readonly orgPath: string;
    private readonly checksPath: string;
    private readonly invitedPath: string;
    private readonly membersPath: string;
    private readonly magicLinkPath: string;
    private readonly createNewUserAccountPath: string;
    private readonly userPath: string;

    constructor(baseUrl: string, headerRetrievalFunction?: () => { [index: string]: string }) {
        super(baseUrl, headerRetrievalFunction);
        this.authPath = '/v1/auth';
        this.mePath = '/v1/me';
        this.invPath = '/v1/invitations';
        this.orgPath = '/v1/organization';
        this.checksPath = '/v1/checks';
        this.invitedPath = '/invited';
        this.membersPath = '/members';
        this.magicLinkPath = '/v1/magic_link';
        this.createNewUserAccountPath = '/v1/create_new_user_account';
        this.userPath = '/v1/user';
    }

    // Magic link
    public async magicLink(email: string, preferred_language: string, mCaptchaToken: string, useAlternativeEmailService?: boolean) {
        const response = await axios.post(`${this.baseUrl}${this.magicLinkPath}`, {
            email: email,
            preferred_language: preferred_language,
            m_captcha_token: mCaptchaToken,
            use_alternate_email_service: useAlternativeEmailService
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data;
    }

    // Request verification characters
    public async requestVerificationCharacters() {
        const response = await axios.get(`${this.baseUrl}/v1/request_characters/get-characters`);

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data;
    }

    // Magic link
    public async createNewUserAccount(email: string, preferred_language: string) {
        const response = await axios.post(`${this.baseUrl}${this.createNewUserAccountPath}`, {
            email: email,
            preferred_language: preferred_language
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data;
    }

    // Authentication Controller
    public async authUser(magicLinkToken: string) {
        const response = await axios.post(`${this.baseUrl}${this.authPath}`, {
            magic_link_token: magicLinkToken
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        localStorage.setItem('ivAccessToken', response.data.token);

        return response.data.language;
    }

    public async authUserToOrg(orgId: string) {
        const response = await axios.get(`${this.baseUrl}${this.authPath}/${orgId}`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        localStorage.setItem('ivAccessToken', response.data.token);
    }

    // MS Authentication Controller
    public async authUserMS(microsoftBearerToken: string) {
        const response = await axios.post(`${this.baseUrl}${this.authPath}/ms`, {
            microsoftBearerToken: microsoftBearerToken
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        localStorage.setItem('ivAccessToken', response.data.token);
    }

    // Show Controller
    public async getUser() {
        const response = await axios.get(`${this.baseUrl}${this.mePath}/info`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.UserDto;
    }

    public async updateUser(
        name?: string,
        profile_pic?: string,
        position?: string,
        preferred_language?: string,
        camera_disabled?: boolean,
        mic_disabled?: boolean,
        personal_meeting_corner?: {
            room_id?: string;
            top_image_index?: number;
            bottom_image_index?: number;
            widgets_list?: any[];
        },
        user_status?: {
            status?: string;
            location?: string | null;
            phrase?: string;
            duration?: number | null;
            endTime?: number | null;
            durationMinutes?: number | null;
            returnDate?: string | null;
            returnTime?: string | null;
            absenceComment?: string | null;
            emergencyContact?: any;
        }
    ) {
        const device_settings_to_request =
            camera_disabled != undefined && mic_disabled != undefined ? { camera_disabled: camera_disabled, mic_disabled: mic_disabled } : undefined;
        const response = await axios.patch(
            `${this.baseUrl}${this.mePath}/update`,
            {
                fields: {
                    name: name,
                    profile_pic: profile_pic,
                    position: position,
                    preferred_language: preferred_language,
                    device_settings: device_settings_to_request,
                    personal_meeting_corner: personal_meeting_corner,
                    user_status: user_status
                }
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.UserDto;
    }

    public async updateHiddenDashboardWidgets(hiddenWidgetIds: string[]) {
        const response = await axios.patch(
            `${this.baseUrl}${this.mePath}/update`,
            { fields: { hidden_dashboard_widgets: hiddenWidgetIds } },
            { headers: this.getHeaders() }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.UserDto;
    }

    public async videoDisplayedToUser() {
        const response = await axios.patch(
            `${this.baseUrl}${this.mePath}/update`,
            {
                fields: {
                    videoDisplayed: true
                }
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.UserDto;
    }

    public async showUserJoinedOrgs() {
        const response = await axios.get(`${this.baseUrl}${this.mePath}/orgs`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.UserJoinedOrgsDto;
    }

    public async userJoinOrg(orgId: string, role: 'user' | 'manager') {
        const response = await axios.patch(
            `${this.baseUrl}${this.mePath}/join-organization`,
            {
                orgId: orgId,
                role: role
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        localStorage.setItem('ivAccessToken', response.data.token);
    }

    public async userQuitOrg(orgId: string) {
        const response = await axios.patch(
            `${this.baseUrl}${this.mePath}/quit-organization`,
            {
                orgId: orgId
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.UserJoinedOrgsDto;
    }

    public async showUserPendingInvitations() {
        const response = await axios.get(`${this.baseUrl}${this.mePath}/pending-invitations`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.UserPendingInvitationsDto;
    }

    // Invitations Controller
    public async createInvitation(
        campusId: string,
        hosts: string[],
        event_name: string,
        type: string,
        repeat: 'no repeat' | 'weekly' | 'daily',
        from: string,
        to: string,
        areaId: string,
        roomId: string,
        welcome_message: string
    ) {
        const response = await axios.post(
            `${this.baseUrl}${this.invPath}`,
            {
                campusId: campusId,
                hosts: hosts,
                event_name: event_name,
                type: type,
                repeat: repeat,
                open_from: from,
                open_to: to,
                default_destination: {
                    areaId: areaId,
                    roomId: roomId
                },
                welcome_message: welcome_message
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 201) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.InvitationDto;
    }

    public async showUserInvitationsList() {
        const response = await axios.get(`${this.baseUrl}${this.invPath}`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ListInvitationsDto;
    }

    public async showInvitation(custom_url?: string, invitationId?: string) {
        const response = await axios.post(`${this.baseUrl}${this.invPath}/show`, {
            fields: {
                custom_url,
                invitationId
            }
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.InvitationDto;
    }

    public async showAccessPass(accessPassId: string) {
        const response = await axios.get(`${this.baseUrl}${this.invPath}/access_pass/${accessPassId}`);

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as any;
    }

    public async updateCustomizedVideoUrl(invitationId: string, customized_video: string) {
        const response = await axios.patch(
            `${this.baseUrl}${this.invPath}/customized-video-url/${invitationId}`,
            {
                customized_video: customized_video
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }
        return response.data as Dto.InvitationDto;
    }

    public async updateCustomUrl(invitationId: string, custom_path: string) {
        const response = await axios.patch(
            `${this.baseUrl}${this.invPath}/custom-url/${invitationId}`,
            {
                custom_path: custom_path
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }
        return response.data as Dto.InvitationDto;
    }

    public async deleteInvitation(invitationId: string) {
        const response = await axios.delete(`${this.baseUrl}${this.invPath}/${invitationId}`, {
            headers: this.getHeaders()
        });

        if (response.status != 204) {
            throw new Error(response.statusText);
        }

        return response.statusText;
    }

    // Visitor Authentication
    public async authVisitor(name: string, invitationId: string) {
        const response = await axios.post(`${this.baseUrl}${this.authPath}/invitation/${invitationId}`, {
            name: name
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        localStorage.setItem('ivAccessToken', response.data.token);
    }

    public async confirmVisitor(areaId: string, campusId: string, roomId: string, invitationId: string, visitorId: string): Promise<string | undefined> {
        const response = await axios.post(
            `${this.baseUrl}${this.authPath}/invitation/${invitationId}/confirm/${visitorId}`,
            {
                roomId: roomId,
                areaId: areaId,
                campusId: campusId
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        if (typeof response.data.token == 'string') return response.data.token;
    }

    public async autoConfirmVisitor(name: string, invitationId: string) {
        const response = await axios.post(`${this.baseUrl}${this.authPath}/invitation/${invitationId}/auto-confirm`, {
            name: name
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        localStorage.setItem('ivAccessToken', response.data.token);
    }

    // Access Pass Authentication

    public async showAccessPassBySlug(slug: string) {
        const response = await axios.get(`${this.baseUrl}/v1/access_passes/slug/${slug}`);

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as any;
    }

    public async showAccessPassById(accessPassId: string) {
        const response = await axios.get(`${this.baseUrl}/v1/access_passes/${accessPassId}`);

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as any;
    }

    public async authAccessPass(name: string, slug: string) {
        const response = await axios.post(`${this.baseUrl}${this.authPath}/access_pass/${slug}`, {
            name: name
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        localStorage.setItem('ivAccessToken', response.data.token);
    }

    public async confirmAccessPass(areaId: string, campusId: string, roomId: string, accessPassId: string, visitorId: string) {
        const response = await axios.post(
            `${this.baseUrl}${this.authPath}/access_pass/${accessPassId}/confirm/${visitorId}`,
            {
                roomId: roomId,
                areaId: areaId,
                campusId: campusId
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }
        if (typeof response.data.token == 'string') return response.data.token;
    }

    public async autoConfirmAccessPass(name: string, access_pass_id: string) {
        const response = await axios.post(`${this.baseUrl}${this.authPath}/access_pass/${access_pass_id}/auto-confirm`, {
            name: name
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        localStorage.setItem('ivAccessToken', response.data.token);
    }

    public useConfirmationToken(confirmationToken: string): boolean {
        const currentToken = localStorage.getItem('ivAccessToken');
        if (!currentToken) return false;
        const accessToken = currentToken?.split(':')[0];

        const nextToken = accessToken + ':' + confirmationToken;
        localStorage.setItem('ivAccessToken', nextToken);
        return true;
    }

    // Visitor List
    public async getVisitorsList(campusId: string) {
        const response = await axios.get(`${this.baseUrl}/v1/visitor/${campusId}`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data;
    }

    public async deleteVisitor(visitorId: string) {
        const response = await axios.delete(`${this.baseUrl}/v1/visitor/${visitorId}`, {
            headers: this.getHeaders()
        });

        if (response.status !== 200) {
            throw new Error(response.statusText);
        }

        return response.data;
    }

    // Organization Controller
    public async createOrg(
        orgName: string,
        orgSize: '1-25' | '25-50' | '50-100' | '100-500' | 'more than 500',
        locationsCount: '1-5' | '5-10' | '10-20' | 'more than 20'
    ) {
        const response = await axios.post(
            `${this.baseUrl}${this.orgPath}`,
            {
                orgName: orgName,
                orgSize: orgSize,
                locationsCount: locationsCount
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 201) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.OrganizationDto;
    }

    public async updateOrg(
        orgName?: string,
        orgLogo?: string,
        orgColors?: string[],
        lobby_background_image?: string,
        visitor_entrance_background_image?: string,
        visitor_entrance_video_url?: string,
        visitor_farewell_message?: string,
        orgSize?: '1-25' | '25-50' | '50-100' | '100-500' | 'more than 500',
        locationsCount?: '1-5' | '5-10' | '10-20' | 'more than 20',
        dashboard_background_image?: number,
        dashboard_widget_providers?: string[],
        zammad_configuration?: { is_zammad_enable: true; zammad_base_url: string } | Record<string, never>,
        openproject_configuration?: { is_openproject_enable: true; openproject_base_url: string } | Record<string, never>,
        zammad_ticket_configuration?: Dto.ZammadTicketConfigurationDto | Record<string, never>,
        documenso_configuration?: { is_documenso_enable: true; documenso_base_url: string } | Record<string, never>,
        colibo_configuration?: Dto.ColiboConfigurationWrite
    ) {
        const response = await axios.patch(
            `${this.baseUrl}${this.orgPath}`,
            {
                fields: {
                    orgName: orgName,
                    orgLogo: orgLogo,
                    orgColors: orgColors,
                    lobby_background_image: lobby_background_image,
                    visitor_entrance_background_image: visitor_entrance_background_image,
                    visitor_entrance_video_url: visitor_entrance_video_url,
                    visitor_farewell_message: visitor_farewell_message,
                    orgSize: orgSize,
                    locationsCount: locationsCount,
                    dashboard_background_image: dashboard_background_image,
                    dashboard_widget_providers: dashboard_widget_providers,
                    zammad_configuration: zammad_configuration,
                    openproject_configuration: openproject_configuration,
                    zammad_ticket_configuration: zammad_ticket_configuration,
                    documenso_configuration: documenso_configuration,
                    colibo_configuration: colibo_configuration
                }
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.OrganizationDto;
    }

    public async showOrg() {
        const response = await axios.get(`${this.baseUrl}${this.orgPath}`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.OrganizationDto;
    }

    public async getOrgBranding(orgId: string) {
        const response = await axios.get(`${this.baseUrl}${this.orgPath}/${orgId}/branding`);

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.OrgBrandingDto;
    }

    public async showOrgInvited() {
        const response = await axios.get(`${this.baseUrl}${this.orgPath}${this.invitedPath}`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ShowOrgInvitedDto;
    }

    public async newInvited(email: string, role: 'user' | 'manager', language: string, type?: 'anyone_bulk') {
        const response = await axios.post(
            `${this.baseUrl}${this.orgPath}${this.invitedPath}`,
            {
                email: email,
                role: role,
                language: language,
                type: type ? type : 'regular'
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ShowOrgInvitedDto;
    }

    public async emailHasSponsor(email: string) {
        const response = await axios.post(
            `${this.baseUrl}${this.orgPath}${this.invitedPath}/sponsor`,
            {
                email: email
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.EmailHasSponsor;
    }

    public async deleteInvited(email: string) {
        const response = await axios.patch(
            `${this.baseUrl}${this.orgPath}${this.invitedPath}`,
            {
                email: email
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ShowOrgInvitedDto;
    }

    public async declineInvitationToJoinOrg(email: string, invOrgId: string) {
        const response = await axios.patch(
            `${this.baseUrl}${this.orgPath}${this.invitedPath}/decline`,
            {
                email: email,
                invOrgId: invOrgId
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ShowOrgInvitedDto;
    }

    public async showMembers() {
        const response = await axios.get(`${this.baseUrl}${this.orgPath}${this.membersPath}`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ShowMembersListDto;
    }

    public async showOutOfOfficeMembers() {
        const response = await axios.get(`${this.baseUrl}${this.orgPath}${this.membersPath}/out-of-office`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data;
    }

    public async changeMemberRole(newRole: 'user' | 'manager', userId: string) {
        const response = await axios.patch(
            `${this.baseUrl}${this.orgPath}${this.membersPath}/${userId}`,
            {
                newRole: newRole
            },
            {
                headers: this.getHeaders()
            }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ChangeMemberRoleMessage;
    }

    public async showMemberProfile(userId: string) {
        const response = await axios.get(`${this.baseUrl}${this.orgPath}${this.membersPath}/${userId}`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ShowMemberProfileDto;
    }

    public async removeMember(userId: string) {
        const response = await axios.delete(`${this.baseUrl}${this.orgPath}${this.membersPath}/${userId}`, {
            headers: this.getHeaders()
        });

        if (response.status != 204) {
            throw new Error(response.statusText);
        }
    }

    public async showBilling() {
        const response = await axios.get(`${this.baseUrl}${this.orgPath}/billing`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data;
    }

    public async archiveOrg() {
        const response = await axios.delete(`${this.baseUrl}${this.orgPath}`, {
            headers: this.getHeaders()
        });

        if (response.status != 204) {
            throw new Error(response.statusText);
        }
    }

    // Checks Controller
    public async checkSetupCompleted() {
        const response = await axios.get(`${this.baseUrl}${this.checksPath}/setup_completed`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.CheckSetupCompletedDto;
    }

    // SAML Authentication Controller
    public async authUserSAML(samlToken: string, configId: string) {
        const response = await axios.post(`${this.baseUrl}${this.authPath}/saml/authenticate`, {
            samlToken: samlToken,
            samlConfigId: configId
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        localStorage.setItem('ivAccessToken', response.data.token);
    }

    // user controller
    public async getUserPersonalMeetingCornerSettings(userId: string) {
        if (!userId || userId === 'undefined') {
            throw new Error('userId is required and cannot be "undefined"');
        }
        const response = await axios.get(`${this.baseUrl}${this.userPath}/personal-meeting-corner-info/${userId}`, { headers: this.getHeaders() });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data;
    }
    public async getUserStatusInfo(userId: string) {
        const response = await axios.get(`${this.baseUrl}${this.userPath}/user-status/${userId}`, { headers: this.getHeaders() });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data;
    }

    // Zammad integration endpoints
    public async getZammadOverviews() {
        const response = await axios.get(`${this.baseUrl}/v1/zammad/overviews`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ZammadOverviewDto[];
    }

    public async getZammadOverviewTickets(link: string, fields: string[]) {
        const fieldsParam = fields.join(',');
        const response = await axios.get(`${this.baseUrl}/v1/zammad/overviews/${link}/tickets?fields=${fieldsParam}`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ZammadOverviewTicketsDto;
    }

    public async getZammadTicketAttributes() {
        const response = await axios.get(`${this.baseUrl}/v1/zammad/ticket-attributes`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ZammadTicketAttributeDto[];
    }

    public async getZammadStatus() {
        const response = await axios.get(`${this.baseUrl}/v1/zammad/status`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ZammadStatusDto;
    }

    public async connectZammadToken(token: string) {
        const response = await axios.patch(`${this.baseUrl}/v1/zammad/connect`, { token }, { headers: this.getHeaders() });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as { is_connected: boolean };
    }

    public async disconnectZammadToken() {
        const response = await axios.delete(`${this.baseUrl}/v1/zammad/disconnect`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as { is_connected: boolean };
    }

    public async getZammadTicketConfig() {
        const response = await axios.get(`${this.baseUrl}/v1/zammad-ticket/config`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ZammadTicketWidgetConfigDto;
    }

    public async sendZammadTicketEmail(input: { category: string; title: string; body: string }) {
        const response = await axios.post(`${this.baseUrl}/v1/zammad-ticket/tickets`, input, { headers: this.getHeaders() });

        if (response.status != 201) {
            throw new Error(response.statusText);
        }

        return response.data as { sent: boolean; reference?: string };
    }

    // OpenProject integration endpoints. Upstream failures come back as 422/502 with a `code`
    // (see OpenProjectErrorCode) — never 401, which is reserved for the ivCAMPUS session itself.
    public async getOpenProjectStatus() {
        const response = await axios.get(`${this.baseUrl}/v1/openproject/status`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.OpenProjectStatusDto;
    }

    public async connectOpenProjectToken(token: string) {
        const response = await axios.patch(`${this.baseUrl}/v1/openproject/connect`, { token }, { headers: this.getHeaders() });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as { is_connected: boolean; openproject_user_name: string };
    }

    public async disconnectOpenProjectToken() {
        const response = await axios.delete(`${this.baseUrl}/v1/openproject/disconnect`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as { is_connected: boolean };
    }

    public async getOpenProjectWorkPackages(view: Dto.OpenProjectWorkPackageView, page: number) {
        const response = await axios.get(`${this.baseUrl}/v1/openproject/work_packages`, {
            params: { view, page },
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.OpenProjectWorkPackagesDto;
    }

    // Documenso integration endpoints. Upstream failures come back as 4xx/502 with a `code`
    // (see DocumensoErrorCode) — never 401, which is reserved for the ivCAMPUS session itself.
    public async getDocumensoStatus() {
        const response = await axios.get(`${this.baseUrl}/v1/documenso/status`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.DocumensoStatusDto;
    }

    public async connectDocumensoToken(token: string) {
        const response = await axios.patch(`${this.baseUrl}/v1/documenso/connect`, { token }, { headers: this.getHeaders() });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as { is_connected: boolean };
    }

    public async disconnectDocumensoToken() {
        const response = await axios.delete(`${this.baseUrl}/v1/documenso/disconnect`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as { is_connected: boolean };
    }

    public async getDocumensoEnvelopes() {
        const response = await axios.get(`${this.baseUrl}/v1/documenso/envelopes`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.DocumensoEnvelopesDto;
    }

    public async createDocumensoEnvelope(input: {
        file: File;
        title: string;
        recipients: { email: string; name: string }[];
        includeMe: boolean;
        language: 'en' | 'de';
    }) {
        const formData = new FormData();
        formData.append('file', input.file);
        formData.append('title', input.title);
        formData.append('recipients', JSON.stringify(input.recipients));
        formData.append('include_me', String(input.includeMe));
        formData.append('language', input.language);

        const response = await axios.post(`${this.baseUrl}/v1/documenso/envelopes`, formData, {
            headers: { ...this.getHeaders(), 'Content-Type': 'multipart/form-data' }
        });

        if (response.status != 201) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.DocumensoEnvelopeSummaryDto;
    }

    public async getDocumensoEditor(id: string) {
        const response = await axios.get(`${this.baseUrl}/v1/documenso/envelopes/${encodeURIComponent(id)}/editor`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.DocumensoEditorDto;
    }

    public async getDocumensoPdf(id: string) {
        const response = await axios.get(`${this.baseUrl}/v1/documenso/envelopes/${encodeURIComponent(id)}/pdf`, {
            headers: this.getHeaders(),
            responseType: 'arraybuffer'
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as ArrayBuffer;
    }

    public async sendDocumensoEnvelope(id: string, fields: Dto.DocumensoFieldInput[], language: 'en' | 'de') {
        const response = await axios.post(
            `${this.baseUrl}/v1/documenso/envelopes/${encodeURIComponent(id)}/send`,
            { fields, language },
            { headers: this.getHeaders() }
        );

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.DocumensoEnvelopeSummaryDto;
    }

    public async discardDocumensoEnvelope(id: string) {
        const response = await axios.delete(`${this.baseUrl}/v1/documenso/envelopes/${encodeURIComponent(id)}`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as { deleted: boolean };
    }

    // Colibo integration endpoints. Same error contract as OpenProject: upstream failures are 422/502
    // with a `code` (see ColiboErrorCode), never 401.
    public async getColiboStatus() {
        const response = await axios.get(`${this.baseUrl}/v1/colibo/status`, { headers: this.getHeaders() });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ColiboStatusDto;
    }

    public async getColiboAuthorizeUrl(redirectUri: string) {
        const response = await axios.post(`${this.baseUrl}/v1/colibo/authorize_url`, { redirect_uri: redirectUri }, { headers: this.getHeaders() });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return (response.data as { authorize_url: string }).authorize_url;
    }

    public async connectColibo(code: string, state: string) {
        const response = await axios.patch(`${this.baseUrl}/v1/colibo/connect`, { code, state }, { headers: this.getHeaders() });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as { is_connected: boolean; colibo_user_name: string };
    }

    public async disconnectColibo() {
        const response = await axios.delete(`${this.baseUrl}/v1/colibo/disconnect`, { headers: this.getHeaders() });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as { is_connected: boolean };
    }

    public async getColiboFeed(pageToken?: string | null) {
        const response = await axios.get(`${this.baseUrl}/v1/colibo/feed`, {
            params: pageToken ? { page_token: pageToken } : {},
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.ColiboFeedDto;
    }

    public async getMatrixSession(options: { clientInstanceId: string; forceNewDevice?: boolean }) {
        const response = await axios.get(`${this.baseUrl}${this.mePath}/matrix-session`, {
            headers: this.getHeaders(),
            params: {
                client_instance_id: options.clientInstanceId,
                ...(options.forceNewDevice ? { force_new_device: true } : {})
            }
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.MatrixSessionDto;
    }

    public async destroyMatrixSession(options: { clientInstanceId: string }): Promise<void> {
        const response = await axios.delete(`${this.baseUrl}${this.mePath}/matrix-session`, {
            headers: this.getHeaders(),
            params: { client_instance_id: options.clientInstanceId }
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }
    }

    public async getMatrixPassword() {
        const response = await axios.get(`${this.baseUrl}${this.mePath}/matrix-session/password`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.MatrixPasswordDto;
    }

    public async getMatrixSecretStorageKey() {
        const response = await axios.get(`${this.baseUrl}${this.mePath}/matrix-session/secret-storage-key`, {
            headers: this.getHeaders()
        });

        if (response.status != 200) {
            throw new Error(response.statusText);
        }

        return response.data as Dto.SecretStorageKeyDto;
    }
}
